import React from 'react';

interface PageInfo {
    endCursor: string;
    hasNextPage: boolean;
    startCursor: string;
    hasPreviousPage: boolean;
}

interface PaginationState {
    first: number | null;
    after: string | null;
    last: number | null;
    before: string | null;
}

interface GraphQLStateBasedPaginationProps {
    paginationState: PaginationState;
    setPaginationState: React.Dispatch<React.SetStateAction<PaginationState>>;
    pageInfo: PageInfo;
    loading?: boolean;
}

const GraphQLStateBasedPagination: React.FC<GraphQLStateBasedPaginationProps> = ({
    paginationState,
    setPaginationState,
    pageInfo,
    loading = false
}) => {
    // Get page size from pagination state
    const getPageSize = () => {
        return paginationState.first || paginationState.last || 20;
    };

    const resetPagination = () => {
        setPaginationState({
            first: getPageSize(),
            after: null,
            last: null,
            before: null
        });
    };

    const handlePageChange = (direction: "next" | "prev") => {
        if (direction === "next") {
            setPaginationState({
                first: getPageSize(),
                after: pageInfo.endCursor,
                last: null,
                before: null
            });
        } else {
            setPaginationState({
                first: null,
                after: null,
                last: getPageSize(),
                before: pageInfo.startCursor
            });
        }
    };

    const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPaginationState({
            first: newPageSize,
            after: null,
            last: null,
            before: null
        });
    };
    
    // Determine if we can go next or previous
    const canGoNext = !Boolean(paginationState.before) && pageInfo.hasNextPage;
    const canGoPrev = !Boolean(paginationState.after) 
        ? pageInfo.hasPreviousPage 
        : true; // If we have "after", we can always go back

    return (
        <div className="px-10 py-6 border-t bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-gray-600">
                <label htmlFor="itemsPerPage" className="text-sm">Items per page:</label>
                <select
                    id="itemsPerPage"
                    value={getPageSize()}
                    onChange={handleItemsPerPageChange}
                    className="px-3 py-2 border rounded text-sm"
                    disabled={loading}
                >
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>
            </div>
            <div className="flex items-center gap-2">
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        handlePageChange("prev");
                    }}
                    disabled={(!Boolean(paginationState.after) && !pageInfo.hasPreviousPage) || loading}
                    className={`px-3 py-2 rounded-full flex items-center justify-center ${
                        (!Boolean(paginationState.after) && !pageInfo.hasPreviousPage) || loading ? 
                        'bg-gray-300 cursor-not-allowed' : 'bg-[#0CAF60] text-white hover:bg-[#09a055] transition-colors'
                    }`}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        handlePageChange("next");
                    }}
                    disabled={(!Boolean(paginationState.before) && !pageInfo.hasNextPage) || loading}
                    className={`px-3 py-2 rounded-full flex items-center justify-center ${
                        (!Boolean(paginationState.before) && !pageInfo.hasNextPage) || loading ? 
                        'bg-gray-300 cursor-not-allowed' : 'bg-[#0CAF60] text-white hover:bg-[#09a055] transition-colors'
                    }`}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default GraphQLStateBasedPagination;